import React from "react";
import {
    Alert,
    Button,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import {CloudSyncOutlined} from "@mui/icons-material";
import {Tag} from "~/components/tag/Tag";
import type {PtV2Dataset, PtV2ImportRecord} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {
    syncSummary,
    type PtV2WorkpaperState,
    workpaperCoverage,
} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Workpapers.ts";
import {parseImportErrors} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Calc.ts";
import {formatDate, formatInt, MISSING, periodLabel} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Format.ts";
import {usePtV2SyncMutation} from "~/experiments/pages/payment-terms-v2/usePaymentTermsV2Queries.ts";
import {PtV2Card} from "~/experiments/pages/payment-terms-v2/components/PtV2Card.tsx";
import {PtV2ImportPanel} from "~/experiments/pages/payment-terms-v2/components/PtV2ImportPanel.tsx";
import {PtV2ImportHistory} from "~/experiments/pages/payment-terms-v2/components/PtV2ImportHistory.tsx";

const STATE_TAG: Record<PtV2WorkpaperState, {label: string; color: 'success' | 'info' | 'default' | 'warning'}> = {
    updated: {label: 'Updated by the last sync', color: 'success'},
    unchanged: {label: 'Unchanged in the last sync', color: 'info'},
    loaded: {label: 'Loaded', color: 'default'},
    missing: {label: 'Not available', color: 'warning'},
};

type SyncResult = {kind: 'ok'; run: PtV2ImportRecord} | {kind: 'failed'; message: string};

function syncError(e: unknown): string {
    const data = (e as {response?: {status?: number; data?: unknown}}).response?.data;
    const errors = parseImportErrors(data);
    return errors.length ? errors.map(x => x.file ? `${x.file}: ${x.message}` : x.message).join(' · ') : 'The sync failed. Try again.';
}

/**
 * Mithra staff only (`authStore.isMithraStaffGroup`; the backend enforces it too): sync from Hermes (BigQuery),
 * the zip fallback, the run history, and every workpaper the page can use: loaded or not, what the last sync did
 * with it, and where it shows.
 */
export const PtV2DataManagement: React.FC<{datasets: PtV2Dataset[]; lastSync: PtV2ImportRecord | null}> = (
    {datasets, lastSync},
) => {
    const syncM = usePtV2SyncMutation();
    const [result, setResult] = React.useState<SyncResult | null>(null);

    const sync = async () => {
        setResult(null);
        try {
            setResult({kind: 'ok', run: await syncM.sync()});
        } catch (e) {
            setResult({kind: 'failed', message: syncError(e)});
        }
    };
    const rows = workpaperCoverage(datasets, lastSync);

    return <PtV2Card title="Data management" right={<Tag color="info" label="Mithra staff only"/>}>
        <div className="ptv2-import">
            <div className="ptv2-eyebrow">Hermes</div>
            <Typography variant="body2" className="ptv2-muted">
                Pull the latest confirmed workpapers that Hermes published to BigQuery. A nightly job does the same.
            </Typography>
            <div className="ptv2-import-row">
                <Button variant="contained" size="small" startIcon={<CloudSyncOutlined/>}
                        disabled={syncM.isPending} onClick={() => void sync()}>
                    Sync from Hermes
                </Button>
            </div>
            {syncM.isPending && <LinearProgress/>}
            {result?.kind === 'ok' && <Alert severity="success">
                Synced from Hermes. {syncSummary(result.run)} See Workpapers below for what each one feeds.
            </Alert>}
            {result?.kind === 'failed' && <Alert severity="error">{result.message}</Alert>}

            <div className="ptv2-eyebrow">Zip upload (fallback)</div>
            <PtV2ImportPanel/>

            <PtV2ImportHistory/>

            <div className="ptv2-eyebrow">Workpapers</div>
            <Typography variant="body2" className="ptv2-muted">
                Every workpaper this page can use. The tabs and sections listed show no data while it is not available.
            </Typography>
            <TableContainer>
                <Table size="small" aria-label="Workpapers">
                    <TableHead>
                        <TableRow>
                            <TableCell>Workpaper</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Used in</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell>Published</TableCell>
                            <TableCell>Period</TableCell>
                            <TableCell align="right">Rows</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(r => <TableRow key={r.dataset}>
                            <TableCell>
                                <div>{r.loaded?.title ?? r.label}</div>
                                <div className="ptv2-mono ptv2-faint">
                                    {r.loaded ? `${r.loaded.slug}${r.loaded.version != null ? ` v${r.loaded.version}` : ''}`
                                        : r.inBigQuery ? 'not published in Hermes' : 'zip upload only'}
                                </div>
                            </TableCell>
                            <TableCell><Tag color={STATE_TAG[r.state].color} variant="outlined" label={STATE_TAG[r.state].label}/></TableCell>
                            <TableCell>{r.usedIn.join(', ') || <span className="ptv2-muted">Not shown on this page</span>}</TableCell>
                            <TableCell>
                                {r.loaded ? <Tag color={r.loaded.source === 'bigquery' ? 'info' : 'default'} variant="outlined"
                                                 label={r.loaded.source === 'bigquery' ? 'BigQuery' : 'Zip'}/> : MISSING}
                            </TableCell>
                            <TableCell className="ptv2-num">{r.loaded ? formatDate(r.loaded.publishedAt) : MISSING}</TableCell>
                            <TableCell className="ptv2-num">
                                {r.loaded ? periodLabel(r.loaded.periodStart, r.loaded.periodEnd) ?? MISSING : MISSING}
                            </TableCell>
                            <TableCell align="right" className="ptv2-num">{r.loaded ? formatInt(r.loaded.rowCount) : MISSING}</TableCell>
                        </TableRow>)}
                    </TableBody>
                </Table>
            </TableContainer>
            {lastSync?.status === 'succeeded' && lastSync.skipped?.length > 0 && <Typography variant="body2" className="ptv2-muted">
                Published in Hermes but not used by this page: <span className="ptv2-mono">{lastSync.skipped.join(', ')}</span>
            </Typography>}
        </div>
    </PtV2Card>;
};
